import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  HelpCircle, Trophy, Users, Building, MapPin, ChevronRight,
  ArrowLeft, BookOpen, CheckCircle, Info, Lightbulb, GraduationCap, Timer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  HelpCircle,
  Trophy,
  Users,
  Building,
  MapPin,
  BookOpen,
  CheckCircle,
  Info,
  Lightbulb,
  GraduationCap,
  Timer
};

const FormGuide = () => {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/instructions`);
      const data = await res.json();
      if (data.success) {
        setInstructions(data.instructions || []);
        // Handle settings from response - can be at top level or nested
        const guideSettings = data.settings || {
          enabled: data.enabled !== false, // Default to enabled
          show_in_menu: true,
          show_on_form: true,
          page_title: 'JEE Form Guide',
          page_description: 'Step-by-step instructions for filling the JEE College Predictor form'
        };
        setSettings(guideSettings);
      } else {
        // API failed but we have static content, so enable by default
        setSettings({
          enabled: true,
          show_in_menu: true,
          show_on_form: true,
          page_title: 'JEE Form Guide',
          page_description: 'Step-by-step instructions for filling the JEE College Predictor form'
        });
      }
    } catch (error) {
      console.error('Error fetching instructions:', error);
      // On error, still show the static content
      setSettings({
        enabled: true,
        show_in_menu: true,
        show_on_form: true,
        page_title: 'JEE Form Guide',
        page_description: 'Step-by-step instructions for filling the JEE College Predictor form'
      });
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All Topics', icon: BookOpen },
    { id: 'general', label: 'Getting Started', icon: HelpCircle },
    { id: 'rank', label: 'JEE Rank', icon: Trophy },
    { id: 'category', label: 'Categories', icon: Users },
    { id: 'counselling', label: 'Counselling', icon: Building },
    { id: 'rounds', label: 'Rounds', icon: Timer },
    { id: 'quota', label: 'HS/OS Quota', icon: MapPin },
    { id: 'state', label: 'Home State', icon: MapPin }
  ];

  // Static content for Counselling Rounds
  const roundsGuide = {
    id: 'rounds-guide',
    title: 'Understanding Counselling Rounds',
    category: 'rounds',
    icon: 'Timer',
    content: `JoSAA and other counselling bodies conduct multiple rounds to fill seats. Here's what you need to know:

🔵 Round 1 (Most Competitive)
• First round of seat allocation
• Cutoffs are at their strictest
• Best colleges fill most seats here
• Example: If a branch cutoff is 5000, only students with rank ≤5000 will get it

🟢 Rounds 2-5
• Cutoffs gradually relax as students reject seats
• More options become available
• Some students upgrade to better colleges
• Example: Same branch might accept up to rank 5500-6000

🟠 Round 6 (Spot Round)
• Final round for vacant seats
• Most relaxed cutoffs (~25% higher)
• Good opportunity for borderline candidates
• Example: Same branch might accept up to rank 6250

💡 Pro Tips:
• Select your current round in our predictor for accurate predictions
• Round 1 predictions are most conservative
• Later rounds show more opportunities
• Always have backup options across rounds`
  };

  const roundsExamples = {
    id: 'rounds-examples',
    title: 'Round-wise Cutoff Examples',
    category: 'rounds',
    icon: 'Lightbulb',
    content: `Real example: NIT Trichy - Computer Science (General)

📊 Hypothetical Cutoff Progression:
┌─────────┬────────────┬──────────────┐
│ Round   │ Cutoff     │ Relaxation   │
├─────────┼────────────┼──────────────┤
│ Round 1 │ 5,000      │ Base         │
│ Round 2 │ 5,250      │ +5%          │
│ Round 3 │ 5,500      │ +10%         │
│ Round 4 │ 5,750      │ +15%         │
│ Round 5 │ 6,000      │ +20%         │
│ Round 6 │ 6,250      │ +25%         │
└─────────┴────────────┴──────────────┘

🎯 If your rank is 5,300:
• Round 1: ❌ Not eligible (5,300 > 5,000)
• Round 2: ❌ Not eligible (5,300 > 5,250)
• Round 3: ✅ Eligible! (5,300 < 5,500)

This is why selecting the right round matters for accurate predictions!`
  };

  // Static content for HS/OS Quota
  const quotaGuide = {
    id: 'quota-guide',
    title: 'Understanding Home State (HS) vs Other State (OS) Quota',
    category: 'quota',
    icon: 'MapPin',
    content: `NITs and IIITs reserve ~50% seats for Home State (HS) students. This significantly affects your chances!

🏠 Home State (HS) Quota
• Reserved for students whose home state matches the NIT/IIIT location
• Cutoffs are MORE RELAXED (higher closing ranks accepted)
• Easier to get admission compared to OS quota
• Example: Tamil Nadu students have HS quota at NIT Trichy

🌍 Other State (OS) Quota
• For students from states different from the college location
• Cutoffs are STRICTER (~20% lower closing ranks)
• More competitive - need better rank
• Example: Maharashtra student applying to NIT Trichy gets OS quota

🇮🇳 All India (AI) Quota
• IITs follow All India quota - no state preference
• Same cutoff for all students regardless of home state
• GFTIs also mostly follow AI quota

⚠️ Important Notes:
• Your home state is based on your 10th/12th board or domicile certificate
• HS/OS quota applies to JoSAA counselling for NITs and IIITs
• Select your correct home state in our predictor for accurate results`
  };

  const quotaExamples = {
    id: 'quota-examples',
    title: 'HS vs OS Quota - Real Example',
    category: 'quota',
    icon: 'Lightbulb',
    content: `📊 NIT Trichy - Computer Science (General Category)

┌──────────────┬─────────────┬─────────────────────────┐
│ Quota Type   │ Cutoff Rank │ Who Qualifies?          │
├──────────────┼─────────────┼─────────────────────────┤
│ 🏠 HS Quota  │ 5,561       │ Tamil Nadu students     │
│ 🌍 OS Quota  │ 4,448       │ Students from other     │
│              │             │ states                  │
└──────────────┴─────────────┴─────────────────────────┘

🎯 Example: Tamil Nadu student with Rank 5,000

At NIT Trichy (Tamil Nadu):
• HS Cutoff: 5,561 → ✅ HIGH CHANCE (5,000 < 5,561)
• Gets Home State advantage!

At NIT Warangal (Telangana):
• OS Cutoff: 4,448 → ❌ NOT ELIGIBLE (5,000 > 4,448)
• Would need rank below 4,448

🎯 Example: Maharashtra student with Rank 5,000

At NIT Trichy (Tamil Nadu):
• OS Cutoff: 4,448 → ❌ NOT ELIGIBLE (5,000 > 4,448)

At NIT Nagpur (Maharashtra):
• HS Cutoff: ~6,000 → ✅ HIGH CHANCE (5,000 < 6,000)
• Gets Home State advantage here!

💡 Key Takeaway:
HS quota gives ~20% advantage. Always check both HS and OS cutoffs in your predictions!`
  };

  const quotaStrategy = {
    id: 'quota-strategy',
    title: 'Strategic Tips for HS/OS Quota',
    category: 'quota',
    icon: 'Info',
    content: `🎓 Maximize Your Admission Chances with Smart Quota Selection

📋 Strategy 1: Prioritize Your Home State NIT
• You get HS quota advantage (~20% relaxed cutoffs)
• Higher chance of admission
• Example: Bihar student should prioritize NIT Patna

📋 Strategy 2: Target Multiple NITs Strategically
• Apply to NITs where you have HS quota (your state)
• Also apply to NITs in less competitive states with OS quota
• North-Eastern NITs often have higher cutoffs (easier to get)

📋 Strategy 3: Understand the Numbers
• HS Cutoff = Base cutoff (what we show as HS)
• OS Cutoff = Base × 0.80 (20% stricter)
• If your rank is between HS and OS cutoff, you ONLY qualify for HS

📋 Strategy 4: Check Our Predictor Badges
• 🏠 Home State badge = You have HS advantage here
• 🌍 Other State badge = You're competing in OS quota
• 🇮🇳 All India badge = Same cutoff for everyone (IITs)

📋 Strategy 5: Fill Choices Wisely in JoSAA
• Put HS quota options higher in preference
• Don't waste top choices on OS quota where you don't qualify
• Use our "Chance" indicator to prioritize

Remember: A good college with HS quota is often better than reaching for a slightly better college with OS quota!`
  };

  // Combine API instructions with static content
  const allInstructions = activeCategory === 'rounds' 
    ? [roundsGuide, roundsExamples]
    : activeCategory === 'quota'
    ? [quotaGuide, quotaExamples, quotaStrategy]
    : activeCategory === 'all'
    ? [...instructions, roundsGuide, roundsExamples, quotaGuide, quotaExamples, quotaStrategy]
    : instructions.filter(i => i.category === activeCategory);

  const filteredInstructions = allInstructions;

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || HelpCircle;
    return IconComponent;
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-blue-100 text-blue-700 border-blue-200',
      rank: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      category: 'bg-purple-100 text-purple-700 border-purple-200',
      counselling: 'bg-green-100 text-green-700 border-green-200',
      rounds: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      quota: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      state: 'bg-orange-100 text-orange-700 border-orange-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings?.enabled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Info className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Guide Not Available</h2>
            <p className="text-slate-600 mb-4">The form guide is currently not available.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Predictor
            </Button>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-blue-600" />
              <span className="font-semibold text-slate-800">JEE College Predictor</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {settings?.page_title || 'JEE Form Guide'}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {settings?.page_description || 'Step-by-step instructions for filling the JEE College Predictor form'}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className="gap-2"
              >
                <Icon className="w-4 h-4" />
                {cat.label}
              </Button>
            );
          })}
        </div>

        {/* Instructions Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {filteredInstructions.map((instruction, idx) => {
            const Icon = getIcon(instruction.icon);
            return (
              <Card key={instruction.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getCategoryColor(instruction.category)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{instruction.title}</CardTitle>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {categories.find(c => c.id === instruction.category)?.label || instruction.category}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-slate-600 whitespace-pre-line text-sm leading-relaxed">
                    {instruction.content}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredInstructions.length === 0 && (
          <div className="text-center py-12">
            <Info className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No instructions found for this category.</p>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <Card className="max-w-xl mx-auto bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold mb-2">Ready to Predict Your Colleges?</h3>
              <p className="text-white/80 mb-4">
                Use our predictor to find the best colleges based on your JEE rank
              </p>
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => navigate('/')}
                className="gap-2"
              >
                Start Prediction <ChevronRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FormGuide;
